<template>
  <app-modal v-model="isDialogOpened"
             :ok-text="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.documentDownload')"
             size="md"
             @ok="submitForm"
             @cancel="closeDialog"
  >
    <template #title>{{ $t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.downloadSignedFile') }}</template>
    <template #body>
      <form ref="envelopeDownloadForm"
            method="post"
            target="_blank"
      >
        <div class="mb-4">{{ signedDocument.name }}</div>

        <v-checkbox :model-value="true"
                    disabled
                    :label="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.selectSignedDocument')"
                    name="withSignedDoc"
                    density="compact"
                    hide-details
        />

        <template v-if="signedDocument.hasCertificateLocale">
          <div>
            <AppTooltip :disabled="selectLocaleAuthenticationCertificate || !selectAuthenticationCertificate">
              <template #activator="{ props }">
                <div class="d-inline-block" v-bind="props">
                  <v-checkbox v-model="selectLocaleAuthenticationCertificate"
                              :disabled="selectAuthenticationCertificate || selectMergeDocuments"
                              :label="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.selectAuthenticationCertificateWithLanguage', { language: signedDocument.certificateLocale.toUpperCase() })"
                              name="withLocaleCertificate"
                              density="compact"
                              hide-details
                  />
                </div>
              </template>
              <span>{{ $t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.singleOnlyCertificateSelectionTooltip') }}</span>
            </AppTooltip>
          </div>

          <div>
            <AppTooltip :disabled="selectAuthenticationCertificate || !selectLocaleAuthenticationCertificate">
              <template #activator="{ props }">
                <div class="d-inline-block" v-bind="props">
                  <v-checkbox v-if="signedDocument.proofFileURL"
                              v-model="selectAuthenticationCertificate"
                              :disabled="selectLocaleAuthenticationCertificate || selectMergeDocuments"
                              :label="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.selectAuthenticationCertificateWithLanguage', { language: DEFAULT_CERTIFICATE_LANGUAGE })"
                              name="withDefaultCertificate"
                              density="compact"
                              hide-details
                  />
                </div>
              </template>
              <span>{{ $t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.singleOnlyCertificateSelectionTooltip') }}</span>
            </AppTooltip>
          </div>
        </template>
        <v-checkbox v-else-if="signedDocument.proofFileURL"
                    v-model="selectAuthenticationCertificate"
                    :disabled="selectMergeDocuments"
                    :label="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.selectAuthenticationCertificate')"
                    name="withDefaultCertificate"
                    density="compact"
                    hide-details
        />

        <template v-if="isDocumentsMergingOptionDisplayed">
          <v-divider class="my-8" />

          <AppTooltip :disabled="isDocumentsMergingEnabled" :max-width="440">
            <template #activator="{ props }">
              <div class="d-flex align-center" v-bind="props">
                <v-checkbox v-model="selectMergeDocuments"
                            :label="$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.selectAuthenticationCertificate')"
                            :disabled="!isDocumentsMergingEnabled"
                            name="mergeDocuments"
                            class="mt-0 pt-0 d-flex align-center"
                            density="compact"
                            hide-details
                >
                  <template #label>
                    <span :class="{'text-text-disabled': !isDocumentsMergingEnabled}">
                      {{ $t("project.signed-documents.dialogs.SignedDocumentsDownloadDialog.mergeDocuments") }}
                    </span>
                  </template>
                </v-checkbox>
              </div>
            </template>
            <span>{{ disabledDocumentsMergeTooltip }}</span>
          </AppTooltip>
        </template>
      </form>
    </template>
  </app-modal>
</template>

<script>
import { mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { fetchAndDownloadFile } from '@/common/utils/download'
import { dialogMixin } from '@/mixins/dialogMixin'

const DEFAULT_CERTIFICATE_LANGUAGE = 'EN'

export default {
  name: 'SignedDocumentsDownloadDialog',
  components: { AppTooltip },
  mixins: [dialogMixin],
  props: {
    signedDocument: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      dialogIsOpen: false,
      selectAuthenticationCertificate: !!this.signedDocument.proofFileURL,
      selectLocaleAuthenticationCertificate: false,
      selectMergeDocuments: false,
      DEFAULT_CERTIFICATE_LANGUAGE,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    disabledDocumentsMergeTooltip () {
      if (!this.isAnyCertificateSelected) {
        return this.signedDocument.hasCertificateLocale
          ? this.$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.atLeastOneCertificateIsRequiredForDocumentsMergeTooltip')
          : this.$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.certificateIsRequiredForDocumentsMergeTooltip')
      }

      if (
        (this.selectAuthenticationCertificate && !this.signedDocument.hasCombinedCertificate) ||
        (this.selectLocaleAuthenticationCertificate && !this.signedDocument.hasCombinedCertificateLocale)
      ) {
        return this.$t('project.signed-documents.dialogs.SignedDocumentsDownloadDialog.mergedDocumentsNotAvailableForSelectedLanguage')
      }

      return null
    },
    isAnyCertificateSelected () {
      return this.selectAuthenticationCertificate || this.selectLocaleAuthenticationCertificate
    },
    isDocumentsMergingEnabled () {
      return (this.selectAuthenticationCertificate && this.signedDocument.hasCombinedCertificate) ||
        (this.selectLocaleAuthenticationCertificate && this.signedDocument.hasCombinedCertificateLocale)
    },
    isDocumentsMergingOptionDisplayed () {
      return this.signedDocument.hasCombinedCertificate
    },
  },
  methods: {
    setFormActionUrl () {
      if (this.isAnyCertificateSelected) {
        this.$refs.envelopeDownloadForm.action = this.selectMergeDocuments
          ? `${import.meta.env.VUE_APP_API_URL}/room/${this.roomMnemo}/downloads/combined/${this.signedDocument.id}?locale=${this.selectLocaleAuthenticationCertificate}`
          : `${import.meta.env.VUE_APP_API_URL}/room/${this.roomMnemo}/downloads/envelopeFinalZip/${this.signedDocument.id}?locale=${this.selectLocaleAuthenticationCertificate}`
      } else {
        this.$refs.envelopeDownloadForm.action = this.signedDocument.signedDocumentURL
      }
    },
    async submitForm () {
      this.setFormActionUrl()
      try {
        await fetchAndDownloadFile(this.$refs.envelopeDownloadForm, {
          openInNewTab: true,
        })
      } catch (error) {
        console.error('Download failed:', error)
      } finally {
        this.closeDialog()
      }
    },
  },
}
</script>
